<template>
  <div class="flex flex-col gap-6">
    <!-- Back Button & Header -->
    <div class="flex items-center gap-4">
      <button @click="$router.back()" class="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        返回
      </button>
      <div>
        <h1 class="text-2xl font-bold text-slate-800">项目详情</h1>
        <p class="text-sm text-slate-500 mt-1">查看项目完整生命周期和流程状态</p>
      </div>
    </div>

    <!-- Project Basic Info Card -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-4">
          <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-2xl">
            {{ project.name.charAt(0) }}
          </div>
          <div>
            <h2 class="text-xl font-bold text-slate-800">{{ project.name }}</h2>
            <div class="flex items-center gap-4 mt-2">
              <span class="text-sm text-slate-500">编号: <span class="font-medium text-slate-700">{{ project.code }}</span></span>
              <span class="text-sm text-slate-500">负责人: <span class="font-medium text-slate-700">{{ project.leader }}</span></span>
              <span class="text-sm text-slate-500">所属单位: <span class="font-medium text-slate-700">{{ project.institution }}</span></span>
            </div>
          </div>
        </div>
        <div class="flex flex-col items-end gap-2">
          <span class="px-4 py-1.5 rounded-full text-sm font-medium"
            :class="{
              'bg-emerald-50 text-emerald-600': project.status === 'completed',
              'bg-blue-50 text-blue-600': project.status === 'inProgress',
              'bg-slate-100 text-slate-500': project.status === 'notStarted'
            }">
            {{ statusText(project.status) }}
          </span>
          <span class="text-xs text-slate-400">当前阶段: {{ project.currentStage }}</span>
        </div>
      </div>

      <!-- Project Stats -->
      <div class="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-200">
        <div class="text-center">
          <div class="text-2xl font-bold text-slate-800 number-font">{{ project.startDate }}</div>
          <div class="text-xs text-slate-500 mt-1">启动日期</div>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold text-slate-800 number-font">{{ project.duration }}</div>
          <div class="text-xs text-slate-500 mt-1">项目周期</div>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold text-slate-800 number-font">{{ project.budget }}</div>
          <div class="text-xs text-slate-500 mt-1">项目经费</div>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold text-slate-800 number-font">{{ project.progress }}%</div>
          <div class="text-xs text-slate-500 mt-1">完成进度</div>
        </div>
      </div>
    </div>

    <!-- Timeline Section -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center gap-2 mb-6">
        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <h2 class="text-lg font-bold text-slate-800">项目生命周期</h2>
      </div>

      <!-- Timeline -->
      <div class="relative pl-8">
        <!-- Timeline Line -->
        <div class="absolute left-[19px] top-4 bottom-4 w-0.5 bg-slate-200"></div>
        <div class="absolute left-[19px] top-4 w-0.5 bg-gradient-to-b from-blue-500 to-cyan-400 transition-all duration-500"
             :style="{ height: (completedSteps / timeline.length * 100) + '%' }"></div>

        <!-- Timeline Items -->
        <div v-for="(step, index) in timeline" :key="step.key" class="relative mb-6 last:mb-0">
          <!-- Node -->
          <div class="absolute left-[-23px] top-1 w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300"
               :class="[
                 step.status === 'completed' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' :
                 step.status === 'inProgress' ? 'bg-white border-4 border-blue-500 text-blue-500 scale-110 shadow-lg shadow-blue-500/20' :
                 'bg-slate-200 text-slate-400'
               ]">
            <svg v-if="step.status === 'completed'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
            </svg>
            <span v-else class="text-sm font-bold">{{ index + 1 }}</span>
          </div>

          <!-- Content Card -->
          <div class="ml-6 glass-card rounded-2xl p-4 border-2 transition-all duration-300"
               :class="[
                 step.status === 'completed' ? 'border-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent' :
                 step.status === 'inProgress' ? 'border-blue-500 bg-gradient-to-r from-blue-50/80 to-transparent shadow-lg shadow-blue-500/10' :
                 'border-slate-200'
               ]">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-3">
                  <h3 class="font-bold text-slate-800" :class="step.status === 'inProgress' ? 'text-blue-700' : ''">{{ step.name }}</h3>
                  <span class="px-2 py-0.5 rounded text-xs font-medium"
                        :class="{
                          'bg-emerald-50 text-emerald-600': step.status === 'completed',
                          'bg-blue-50 text-blue-600': step.status === 'inProgress',
                          'bg-slate-100 text-slate-400': step.status === 'pending'
                        }">
                    {{ statusText(step.status) }}
                  </span>
                </div>
                <p class="text-sm text-slate-500 mt-1">{{ step.description }}</p>
                
                <!-- Step Details -->
                <div v-if="step.details" class="mt-3 space-y-2">
                  <div v-for="(detail, dIndex) in step.details" :key="dIndex" 
                       class="flex items-center gap-2 text-sm"
                       :class="detail.completed ? 'text-slate-600' : 'text-slate-400'">
                    <svg v-if="detail.completed" class="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <svg v-else class="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span>{{ detail.label }}</span>
                    <span v-if="detail.value" class="text-slate-400">- {{ detail.value }}</span>
                  </div>
                </div>

                <!-- Date & Operator -->
                <div v-if="step.date" class="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                  <span class="text-xs text-slate-400 flex items-center gap-1">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    {{ step.date }}
                  </span>
                  <span v-if="step.operator" class="text-xs text-slate-400 flex items-center gap-1">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    {{ step.operator }}
                  </span>
                </div>
              </div>

              <!-- Action Button -->
              <button v-if="step.status === 'inProgress'" 
                      class="ml-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2">
                进入处理
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Project Data Summary -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center gap-2 mb-6">
        <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
        </div>
        <h2 class="text-lg font-bold text-slate-800">项目数据概览</h2>
      </div>

      <div class="grid grid-cols-3 gap-4">
        <div class="p-4 bg-slate-50 rounded-xl">
          <div class="text-sm text-slate-500 mb-1">申报书审查</div>
          <div class="text-lg font-bold text-slate-800">已通过</div>
          <div class="text-xs text-slate-400 mt-1">审查时间: 2025-01-15</div>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl">
          <div class="text-sm text-slate-500 mb-1">立项评审</div>
          <div class="text-lg font-bold text-slate-800">已通过</div>
          <div class="text-xs text-slate-400 mt-1">评审得分: 85分</div>
        </div>
        <div class="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div class="text-sm text-blue-600 mb-1">任务书审查</div>
          <div class="text-lg font-bold text-blue-700">进行中</div>
          <div class="text-xs text-blue-400 mt-1">待审核</div>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl opacity-50">
          <div class="text-sm text-slate-500 mb-1">中期审查</div>
          <div class="text-lg font-bold text-slate-400">待开始</div>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl opacity-50">
          <div class="text-sm text-slate-500 mb-1">年度审查</div>
          <div class="text-lg font-bold text-slate-400">待开始</div>
        </div>
        <div class="p-4 bg-slate-50 rounded-xl opacity-50">
          <div class="text-sm text-slate-500 mb-1">结题审查</div>
          <div class="text-lg font-bold text-slate-400">待开始</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProjectById, getProjectTimeline } from '../services/indexedDB'
import { initDatabase } from '../utils/dbInitializer'

export default {
  name: 'ProjectDetail',
  data() {
    return {
      project: null,
      timeline: [],
      loading: false
    }
  },
  computed: {
    completedSteps() {
      return this.timeline.filter(step => step.status === 'completed').length
    }
  },
  async created() {
    const projectId = parseInt(this.$route.params.id)
    await this.loadProjectData(projectId)
  },
  methods: {
    async loadProjectData(projectId) {
      this.loading = true
      try {
        // 确保数据库已初始化
        await initDatabase()

        // 从 IndexedDB 加载项目数据
        const projectInfo = await getProjectById(projectId)
        const projectDetails = await getProjectTimeline(projectId)

        if (projectInfo) {
          this.project = projectInfo
        }

        if (projectDetails && projectDetails.timeline) {
          this.timeline = projectDetails.timeline
        } else {
          // 如果没有时间线数据，使用默认空数组
          this.timeline = []
        }
      } catch (error) {
        console.error('加载项目详情失败:', error)
      } finally {
        this.loading = false
      }
    },
    statusText(status) {
      const map = {
        completed: '已完成',
        inProgress: '进行中',
        pending: '待开始'
      }
      return map[status] || status
    }
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) {
        this.loadProjectData(parseInt(newId))
      }
    }
  }
}
</script>
